import { useEffect, useState } from 'react'

import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Stack from 'react-bootstrap/Stack'

import { getCustomFees } from '../../../utilities/api'

function feeAttrs(fee) {
  return fee?.attributes ?? fee ?? {}
}

function feeLabel(fee) {
  const attrs = feeAttrs(fee)
  const name = attrs?.name || 'Custom fee'
  if (attrs?.option === 'percent') {
    return `${name} (${Number(attrs?.feeAmount ?? 0)}%)`
  }
  const amount = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
  }).format(Number(attrs?.feeAmount ?? 0))
  return `${name} (${amount})`
}

function feesFromPackage(eventPackage) {
  if (Array.isArray(eventPackage?.customFees)) return eventPackage.customFees
  if (eventPackage?.customFee?.id) return [eventPackage.customFee]
  return []
}

export default function PackageCustomFee({
  eventPackage,
  organizationUUID,
  onSelect,
  isDisabled,
  title = 'Package custom fees',
  description = 'Each fee is applied once when this package is bought online, on top of the ticket price. Seat count and number of games do not change the fees.',
}) {
  const [fees, setFees] = useState([])
  const [rows, setRows] = useState(() => feesFromPackage(eventPackage))

  useEffect(() => {
    if (!organizationUUID) return undefined
    let cancelled = false
    getCustomFees(organizationUUID)
      .then((res) => {
        if (!cancelled) setFees(res?.data?.data ?? [])
      })
      .catch((err) => console.error(err))
    return () => {
      cancelled = true
    }
  }, [organizationUUID])

  const savedKey = feesFromPackage(eventPackage)
    .map((fee) => fee?.id)
    .filter(Boolean)
    .join(',')

  useEffect(() => {
    setRows(feesFromPackage(eventPackage))
  }, [savedKey])

  const publish = (nextRows) => {
    setRows(nextRows)
    onSelect(
      nextRows
        .filter((fee) => fee?.id)
        .map((fee) => ({
          id: fee.id,
          name: fee.name,
          option: fee.option,
          feeAmount: fee.feeAmount,
        }))
    )
  }

  const handleChange = (index, nextId) => {
    if (!nextId) {
      publish(rows.filter((_, rowIndex) => rowIndex !== index))
      return
    }
    const fee = fees.find((item) => String(item.id) === String(nextId))
    if (!fee) return
    const attrs = feeAttrs(fee)
    const nextRows = rows.slice()
    nextRows[index] = {
      id: fee.id,
      name: attrs.name,
      option: attrs.option,
      feeAmount: attrs.feeAmount,
    }
    publish(nextRows)
  }

  const selectedIds = new Set(
    rows.map((fee) => String(fee?.id || '')).filter(Boolean)
  )

  return (
    <>
      <div className="card-body-heading card-body-heading--sm">
        <Card.Title as="h5">{title}</Card.Title>
        <Card.Subtitle as="h6">{description}</Card.Subtitle>
      </div>
      <Stack gap={3}>
        {rows.map((row, index) => {
          const selectedId = row?.id ? String(row.id) : ''
          return (
            <Stack
              key={`${selectedId || 'new'}-${index}`}
              direction="horizontal"
              gap={2}
            >
              <Form.Select
                aria-label={`Custom fee ${index + 1}`}
                value={selectedId}
                onChange={(e) => handleChange(index, e.target.value)}
                disabled={isDisabled}
              >
                <option value="">Select a custom fee</option>
                {selectedId &&
                  !fees.some((fee) => String(fee.id) === selectedId) && (
                    <option value={selectedId}>
                      {row?.name || 'Selected custom fee'}
                    </option>
                  )}
                {fees.map((fee) => {
                  const taken =
                    String(fee.id) !== selectedId &&
                    selectedIds.has(String(fee.id))
                  if (taken) return null
                  return (
                    <option value={fee.id} key={fee.id}>
                      {feeLabel(fee)}
                    </option>
                  )
                })}
              </Form.Select>
              <Button
                variant="outline-danger"
                onClick={() =>
                  publish(rows.filter((_, rowIndex) => rowIndex !== index))
                }
                disabled={isDisabled}
              >
                Remove
              </Button>
            </Stack>
          )
        })}
        <div>
          <Button
            variant="outline-light"
            onClick={() => setRows([...rows, {}])}
            disabled={isDisabled || rows.some((row) => !row?.id)}
          >
            Add custom fee
          </Button>
        </div>
      </Stack>
    </>
  )
}
